import { useEffect, useState, useCallback } from 'react';
import type { CreateRequestPayload, RequestType } from '../../lib/api';
import { IconSparkles, IconCheck } from '../../components/icons';
import { PillSelector } from '../../components/PillSelector';

const TYPES: { id: RequestType; label: string }[] = [
  { id: 'partner', label: 'Ищу партнёра' },
  { id: 'investor', label: 'Ищу инвестора' },
  { id: 'developer', label: 'Ищу разработчика' },
];

interface CreateRequestFormProps {
  onSubmit: (payload: CreateRequestPayload) => void | Promise<void>;
  onCancel: () => void;
  mode?: 'create' | 'edit';
  initialValues?: Partial<CreateRequestPayload> | null;
  submitLabel?: string;
  /** When true, submit is triggered by MainButton; hide the primary submit button */
  hideSubmitButton?: boolean;
  formRef?: React.RefObject<HTMLFormElement>;
  /** When true, submit button is disabled (e.g. while submitting) */
  isSubmitting?: boolean;
}

export function CreateRequestForm({
  onSubmit,
  onCancel,
  mode = 'create',
  initialValues = null,
  submitLabel,
  hideSubmitButton,
  formRef,
  isSubmitting = false,
}: CreateRequestFormProps) {
  const [type, setType] = useState<RequestType>('developer');
  const [title, setTitle] = useState('');
  const [budget, setBudget] = useState('');
  const [deadline, setDeadline] = useState('');
  const [format, setFormat] = useState('');
  const [description, setDescription] = useState('');
  const [urgent, setUrgent] = useState(false);

  useEffect(() => {
    setType(initialValues?.type ?? 'developer');
    setTitle(initialValues?.title ?? '');
    setBudget(initialValues?.budget ?? '');
    setDeadline(initialValues?.deadline ?? '');
    setFormat(initialValues?.format ?? '');
    setDescription(initialValues?.description ?? '');
    setUrgent(Boolean(initialValues?.urgent));
  }, [initialValues]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const result = onSubmit({
        type,
        title: title.trim() || 'Без названия',
        description: description.trim() || title.trim() || 'Уточню в переписке',
        budget: budget.trim() || null,
        deadline: deadline.trim() || null,
        format: format.trim() || null,
        what_matters: [],
        tools: [],
        urgent,
      });
      if (result && typeof (result as Promise<void>).then === 'function') {
        await (result as Promise<void>);
      }
    },
    [type, title, budget, deadline, format, description, urgent, onSubmit]
  );

  return (
    <form
      id="create-request-form"
      ref={formRef}
      className="create-request-form"
      onSubmit={handleSubmit}
    >
      <div className="create-request-field">
        <label className="create-request-label form-label">Тип заявки</label>
        <PillSelector
          options={TYPES.map((t) => t.label)}
          value={TYPES.find((t) => t.id === type)?.label ?? TYPES[0].label}
          onChange={(label: string) => {
            const next = TYPES.find((t) => t.label === label);
            if (next) setType(next.id);
          }}
        />
      </div>

      <div className="create-request-field">
        <label className="create-request-label form-label" htmlFor="req-title">
          Название
        </label>
        <input
          id="req-title"
          type="text"
          className="create-request-input"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Краткое название заявки"
        />
      </div>

      <div className="create-request-row">
        <div className="create-request-field">
        <label className="create-request-label form-label" htmlFor="req-budget">
          Бюджет
        </label>
          <input
            id="req-budget"
            type="text"
            className="create-request-input"
            value={budget}
            onChange={(e) => setBudget(e.target.value)}
            placeholder="до 20к"
          />
        </div>
        <div className="create-request-field">
        <label className="create-request-label form-label" htmlFor="req-deadline">
          Срок
        </label>
          <input
            id="req-deadline"
            type="text"
            className="create-request-input"
            value={deadline}
            onChange={(e) => setDeadline(e.target.value)}
            placeholder="1 неделя"
          />
        </div>
      </div>

      <div className="create-request-field">
        <label className="create-request-label form-label" htmlFor="req-format">
          Формат
        </label>
        <input
          id="req-format"
          type="text"
          className="create-request-input"
          value={format}
          onChange={(e) => setFormat(e.target.value)}
          placeholder="Готовый продукт / Консультация"
        />
      </div>

      <div className="create-request-field">
        <label className="create-request-label form-label" htmlFor="req-task">
          Описание задачи
        </label>
        <textarea
          id="req-task"
          className="create-request-textarea"
          rows={4}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Опишите, что нужно сделать..."
        />
      </div>
      <div
        className={`urgency-checkbox ${urgent ? 'checked' : ''}`}
        role="button"
        tabIndex={0}
        onClick={() => setUrgent((u) => !u)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setUrgent((u) => !u); } }}
      >
        <div className="urgency-box">
          {urgent ? <IconCheck size={12} /> : null}
        </div>
        <label htmlFor="req-urgent" className="create-request-label create-request-label--inline" style={{ margin: 0, cursor: 'pointer' }}>
          Срочно
        </label>
        <input
          id="req-urgent"
          type="checkbox"
          checked={urgent}
          onChange={(e) => setUrgent(e.target.checked)}
          className="sr-only"
          aria-hidden
        />
      </div>

      <div className="create-request-actions">
        <button type="button" className="btn btn-compact" onClick={onCancel}>
          Отмена
        </button>
        {!hideSubmitButton && (
          <button type="submit" className="form-submit-btn" disabled={isSubmitting}>
            <IconSparkles size={16} /> {submitLabel ?? (mode === 'edit' ? 'Сохранить изменения' : 'Сохранить черновик')}
          </button>
        )}
      </div>
    </form>
  );
}
